import React from 'react';
import { TextField, Button, Grid, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../../stores/store';
import { HistorySearchParam } from '../../../stores/history/api';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { DATE_FORMAT } from '../../../_constants/Const';
import { adjustInputYear, isEmptyData } from '../../../_utils/utils';

type HistorySearchFormProps = {
  params: HistorySearchParam;
  updateParams: (params: HistorySearchParam) => void;
  handleClear: () => void;
  onClickSearch: () => void;
};

const HistorySearchForm: React.FC<HistorySearchFormProps> = ({
  params,
  updateParams,
  handleClear,
  onClickSearch,
}) => {
  const { t } = useTranslation();

  const { userData } = useSelector((state: RootState) => state.userInfo);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    updateParams({
      ...params,
      [name]:
        typeof params[name as keyof HistorySearchParam] === 'number'
          ? Number(value)
          : value,
    });
  };

  const handleInputDateChange = (
    value: string | number | Dayjs | null,
    name: keyof HistorySearchParam,
  ) => {
    let date = '';
    if (value instanceof Object) {
      // format通りの年月日の形式にする
      const formattedDate =
        value.year().toString() +
        '-' +
        Number(value.month() + 1).toString() +
        '-' +
        value.date().toString();
      const adjustedDate = adjustInputYear(dayjs(formattedDate));
      const formatString =
        userData.dateFormat === 2 // DD-MM-YY → DD-MM-YYYY
          ? DATE_FORMAT[1]
          : DATE_FORMAT[userData.dateFormat];
      date = adjustedDate ? dayjs(adjustedDate).format(formatString) : '';
    }

    updateParams({
      ...params,
      [name]: date,
    });
  };

  return (
    <Box sx={{ p: 3, pb: 0, mb: 4 }}>
      <Grid container spacing={2} alignItems="center" justifyContent="center">
        <Grid size={2}>
          <TextField
            fullWidth
            label={t('LBL_SERIAL_NO')}
            name="serialNo"
            size="small"
            value={params.serialNo}
            onChange={handleInputChange}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />
        </Grid>
        <Grid size={2}>
          <TextField
            fullWidth
            label={t('LBL_RMA_NO')}
            name="rmaNo"
            size="small"
            value={params.rmaNo}
            onChange={handleInputChange}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />
        </Grid>
        <Grid size={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={t('LBL_UPDATE_TIME_FROM')}
              value={isEmptyData(params.from) ? null : dayjs(params.from)}
              onChange={(value) => handleInputDateChange(value, 'from')}
              maxDate={dayjs(params.to)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  InputLabelProps: { shrink: true },
                },
                field: {
                  clearable: true,
                  onClear: () => handleInputDateChange('', 'from'),
                },
              }}
              format={DATE_FORMAT[userData.dateFormat]}
            />
          </LocalizationProvider>
        </Grid>
        <Grid size={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={t('LBL_UPDATE_TIME_TO')}
              value={isEmptyData(params.to) ? null : dayjs(params.to)}
              onChange={(value) => handleInputDateChange(value, 'to')}
              minDate={dayjs(params.from)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  InputLabelProps: { shrink: true },
                },
                field: {
                  clearable: true,
                  onClear: () => handleInputDateChange('', 'to'),
                },
              }}
              format={DATE_FORMAT[userData.dateFormat]}
            />
          </LocalizationProvider>
        </Grid>
        {/* 検索とクリアボタン */}
        <Grid size={3}>
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={onClickSearch}
              fullWidth
            >
              {t('BTN_SEARCH')}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleClear}
              fullWidth
            >
              {t('BTN_CLEAR')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HistorySearchForm;
